"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AddressAutocompleteInputV2 = void 0;
const react_1 = __importStar(require("react"));
const config_1 = require("../config");
const Dropdown_hooks_1 = require("../dropdown/Dropdown.hooks");
const DropdownOption_1 = require("../dropdown/DropdownOption");
const google_maps_api_provider_1 = require("../google-maps-api-provider");
const useClickOutsideHandler_1 = require("../utils/useClickOutsideHandler");
const AddressAutocomplete_hooks_1 = require("./AddressAutocomplete.hooks");
const AddressAutocomplete_styles_1 = require("./AddressAutocomplete.styles");
const AddressAutocomplete_utils_1 = require("./AddressAutocomplete.utils");
const AddressAutocompleteInputInner = (props) => {
    const { onAddressChange, restrictToArea, onPredictionsChanged, value, shouldAlwaysOpenOnFocus, renderCustomSuggestionComponent, onBlur, onFocus, spacing, shouldRunOnAddressChangeOnType = true, ...inputProps } = props;
    /**
     * To track the first value of the input, we use the fact that `useState`
     * doesn't update the initial value on subsequent renders
     * */
    const [defaultValue] = (0, react_1.useState)(value);
    const [internalValue, setInternalValue] = (0, react_1.useState)(value);
    const [isOpen, setIsOpened] = (0, react_1.useState)(false);
    const { getPlacePredictions, placesService, placePredictions, isPlacePredictionsLoading } = (0, AddressAutocomplete_hooks_1.usePlacesService)({
        restrictedArea: restrictToArea,
    });
    const placePredictionsWithinRadius = (0, react_1.useMemo)(() => placePredictions.filter((prediction) => (0, AddressAutocomplete_utils_1.isPlaceWithinRadius)(prediction, restrictToArea)), [placePredictions]);
    const googleAutocompleteRef = (0, react_1.useRef)(null);
    const wrapperRef = (0, react_1.useRef)(null);
    const setValueToTheInputElement = (newValue) => {
        if (googleAutocompleteRef.current) {
            googleAutocompleteRef.current.value = newValue;
        }
    };
    const closeSuggestions = () => {
        setIsOpened(false);
        googleAutocompleteRef.current?.blur();
    };
    // --- Event handlers ---
    // These methods describe how an event should be handled.
    const onSuggestionSelected = (description, placeId) => {
        placesService?.getDetails({
            placeId,
            fields: ["formatted_address", "geometry", "address_components"],
        }, (placeDetails, status) => {
            if (!placeDetails || status !== "OK") {
                return;
            }
            const newLocation = (0, AddressAutocomplete_utils_1.parsePlaceResultResponse)(description, placeDetails);
            setInternalValue(newLocation);
            setValueToTheInputElement(newLocation.name);
            if (onAddressChange) {
                onAddressChange(newLocation);
            }
            closeSuggestions();
        });
    };
    const { focused, setFocused, handleArrowNavigation } = (0, Dropdown_hooks_1.useDropdownArrowKeysNavigation)(false, true, placePredictionsWithinRadius.map((place) => ({
        label: place.description,
        value: place.place_id,
    })), (option) => {
        onSuggestionSelected(option.label, option.value.toString());
    }, setIsOpened);
    const onInputChange = (inputValue) => {
        setFocused(undefined);
        setIsOpened(!!googleAutocompleteRef?.current?.value);
        // If the input has an exact match from results, we enforce it
        // This accounts for manually entered/modified addresses (copy/paste, delete/undos, etc)
        const resultMatch = placePredictionsWithinRadius.find((prediction) => prediction.description === inputValue);
        if (resultMatch) {
            onSuggestionSelected(resultMatch.description, resultMatch.place_id);
        }
        if (onAddressChange && shouldRunOnAddressChangeOnType) {
            onAddressChange({ name: inputValue, countryIsoCode: "undefined" });
        }
        // @TODO: This is a temporary fix
        // The whole concept of shouldRunOnAddressChangeOnType is probably not a good idea.
        //
        // Normally predictions get fetched in onValueChangedFromParent (because we need new predictions even when we drag the map in MapView)
        // But that doesn't happen if `shouldRunOnAddressChangeOnType` is false.
        // So, in that case, we need to fetch predictions here as well.
        if (!shouldRunOnAddressChangeOnType) {
            getPlacePredictions(inputValue);
        }
    };
    const onValueChangedFromParent = (0, react_1.useCallback)((newValue) => {
        setInternalValue(newValue);
        setValueToTheInputElement(newValue?.name || "");
        if (newValue?.name) {
            getPlacePredictions(newValue.name);
        }
    }, []);
    const handleFocus = (event) => {
        if (!googleAutocompleteRef.current) {
            throw Error("Ref is not set for google autocomplete input.");
        }
        if (placePredictionsWithinRadius.length || shouldAlwaysOpenOnFocus) {
            setIsOpened(true);
        }
        if (onFocus) {
            onFocus(event);
        }
    };
    const handleBlur = async (event) => {
        const hasFocusedOut = wrapperRef.current && !wrapperRef.current.matches(":focus-within");
        if (hasFocusedOut) {
            setIsOpened(false);
            if (onBlur) {
                onBlur(event);
            }
        }
    };
    // --- Event handlers end ---
    // --- Effects ---
    (0, react_1.useEffect)(() => {
        if (onPredictionsChanged) {
            onPredictionsChanged(placePredictions);
        }
    }, [placePredictions]);
    (0, react_1.useEffect)(() => {
        onValueChangedFromParent(value);
    }, [value]);
    (0, useClickOutsideHandler_1.useClickOutsideHandler)(wrapperRef, isOpen, setIsOpened);
    // --- Effects end ---
    const isOpenBecauseItHasResults = isOpen && !isPlacePredictionsLoading && placePredictionsWithinRadius.length > 0;
    const isOpenBecauseOfAlwaysOpenOnFocus = isOpen && !!shouldAlwaysOpenOnFocus;
    const isPredictionsDropdownOpen = isOpenBecauseItHasResults || isOpenBecauseOfAlwaysOpenOnFocus;
    /**
     * Copy/paste of the same code in `AddressAutocomplete.hooks.ts`
     * We will remove the `useValidateAddress` hook once V1 and V2
     * autocomplete inputs are unified, so for now we just copy/paste
     * */
    function isAddressValid() {
        const isCurrentAddressDefault = internalValue?.name === defaultValue?.name;
        /**
         * If the value of the address hasn't changed from the default one,
         * then it is implied that the address is valid (was previously allowed to
         * be selected). Without this, on the first render, the `placePredictions`
         * will be empty and any address will be considered invalid.
         * */
        if (isCurrentAddressDefault) {
            return true;
        }
        // Some places descriptions are not in english and special characters do not allow simple comparison between strings
        const predictedPlace = placePredictions.find((place) => (0, AddressAutocomplete_hooks_1.cleanString)(place.description) === (0, AddressAutocomplete_hooks_1.cleanString)(internalValue?.name ?? "") ||
            place.matched_substrings?.length >= 3);
        if (!predictedPlace) {
            return false;
        }
        return internalValue?.name !== "";
    }
    function isInputValid() {
        if (props.isValid != null) {
            return props.isValid;
        }
        if (isOpen) {
            return true;
        }
        if (googleAutocompleteRef.current?.value) {
            return isAddressValid();
        }
        return true;
    }
    return (react_1.default.createElement(AddressAutocomplete_styles_1.DropdownWrapper, { ref: wrapperRef, onKeyDown: handleArrowNavigation, spacing: spacing },
        react_1.default.createElement(AddressAutocomplete_styles_1.StyledInput, { ...inputProps, isAutocompleteDisabled: true, inputRef: googleAutocompleteRef, onChange: onInputChange, onFocus: handleFocus, onBlur: handleBlur, isOpen: isPredictionsDropdownOpen, isPlacePredictionsLoading: isPlacePredictionsLoading, isValid: isInputValid(), ariaInvalid: !isInputValid(), ariaLive: "polite", ariaBusy: isPlacePredictionsLoading }),
        isPredictionsDropdownOpen && (react_1.default.createElement(AddressAutocomplete_styles_1.DropdownBody, { isValid: isInputValid(), tabIndex: isOpen ? -1 : undefined },
            placePredictionsWithinRadius.map(({ description, place_id }, index) => (react_1.default.createElement(DropdownOption_1.DropdownOption, { key: place_id, content: { label: description, value: place_id }, isFocused: focused === index, onSelect: () => {
                    onSuggestionSelected(description, place_id);
                    setFocused(index);
                } }))),
            renderCustomSuggestionComponent && renderCustomSuggestionComponent(closeSuggestions),
            react_1.default.createElement(AddressAutocomplete_styles_1.PoweredByGoogle, null,
                react_1.default.createElement("img", { src: `${config_1.BASE_IMAGE_URL}/site/powered_by_google_on_white_hdpi.png`, alt: "Powered by Google", width: 144, height: 18 }))))));
};
const AddressAutocompleteInputV2 = (props) => {
    const { isLoaded } = (0, google_maps_api_provider_1.useGoogleApi)();
    /**
     * The AddressAutocompleteInputInner component uses the Google Maps api through `usePlacesService`.
     * The `usePlacesService` hook will try to load the Places library as soon as it enters the component tree.
     * This can cause a race condition where the Places library is not loaded yet when the `usePlacesService` hook is called,
     * thereby causing an error when `usePlacesService` tries to new up a `google.maps.places.AutocompleteService` instance.
     *
     * Note: this happens when the `AddressAutocompleteInput` is rendered on the first pass when rendering the page,
     * not when it is rendered on a subsequent pass (e.g. when the user clicks on a button to show the input).
     * */
    if (!isLoaded) {
        // We return a skeleton instead of `null` to avoid the UI jumping around
        return (react_1.default.createElement(AddressAutocomplete_styles_1.DropdownWrapper, { spacing: props.spacing },
            react_1.default.createElement(AddressAutocomplete_styles_1.StyledInput, { isOpen: false })));
    }
    return react_1.default.createElement(AddressAutocompleteInputInner, { ...props });
};
exports.AddressAutocompleteInputV2 = AddressAutocompleteInputV2;
//# sourceMappingURL=AddressAutocompleteV2.js.map